import MainStore from '../MainStore';

/**
 * Build a source chain for a given evidence ID.
 *
 * Walks DOWN through the source tree: starting from the evidence primitive,
 * follows `primitives.source` (or `primitives.ref`) to find child sources.
 * At each summary hop, extracts quotes from that node's structured_summary
 * sections and attaches them to the corresponding child result primitives.
 *
 * When `summaryId` is provided (sidebar popup), only sections whose
 * content/quotes overlap with the viewing quote are considered, so quotes
 * from unrelated sections are excluded.
 *
 * Returns an array of { prim, quotes: string[], depth: number } ordered
 * top-down (ancestor first).  The viewing summary (summaryId) is never
 * included in the chain.
 */
export function buildSourceChain({ evidenceId, quoteForEvidence, summaryId }) {
    const mainstore = MainStore();
    const startPrim = mainstore.primitive(evidenceId);
    if (!startPrim) {
        return { chain: [{ prim: null, quotes: quoteForEvidence, depth: 0 }], missingIds: [evidenceId] };
    }

    const chain = [];
    const missingIds = [];
    const seen = new Set();
    const isSidebar = summaryId != null;

    /**
     * Walk DOWN from `node` through its sources, collecting summary/result
     * nodes.  `parentQuotes` provides the quote for `node` (from the level
     * above).  For each summary encountered, we pull quotes from its
     * structured_summary for its result children.
     */
    const walkDown = (node, parentQuotes, depth) => {
        if (!node || seen.has(node.id)) return;
        if (node.id === summaryId) {
            // Instead of including the viewing summary, walk its structured_summary
            // ids — these are the actual source summaries that fed it.
            const ss = node.referenceParameters?.structured_summary;
            if (Array.isArray(ss)) {
                for (const section of ss) {
                    const secIds = Array.isArray(section.ids) ? section.ids : [];
                    const secIdMap = section.id_map;
                    const secQuotes = Array.isArray(section.quote) ? section.quote : [];
                    for (const sid of secIds) {
                        if (seen.has(sid)) continue;
                        const childPrim = mainstore.primitive(sid);
                        if (!childPrim) continue;
                        // Quote for this child from the viewing summary's id_map
                        let childQuotes = [];
                        if (secIdMap && typeof secIdMap === "object") {
                            const idxs = secIdMap[sid] ?? secIdMap[`${sid}`];
                            if (Array.isArray(idxs)) {
                                childQuotes = idxs.map(i => (secQuotes[i] ?? "").trim()).filter(Boolean);
                            }
                        }
                        walkDown(childPrim, childQuotes.length ? childQuotes : [], depth);
                    }
                }
            }
            return;
        }
        seen.add(node.id);

        chain.push({ prim: node, quotes: parentQuotes, depth });

        if (node.type !== "summary") return;

        // Build a quote map for this summary's children from its structured_summary
        const childQuoteMap = {};
        const ss = node.referenceParameters?.structured_summary;
        if (Array.isArray(ss)) {
            const norm = (s) => (s || "").toLowerCase().replace(/\s+/g, " ").trim();
            const parentNorm = norm(parentQuotes[0] ?? "");
            for (const section of ss) {
                if (isSidebar) {
                    const contentNorm = norm(section.content);
                    const secQuoteText = (Array.isArray(section.quote) ? section.quote : [])
                        .map(q => norm(q)).join(" ");
                    const matches = parentNorm.length > 20 && (
                        contentNorm.includes(parentNorm.slice(0, 40)) ||
                        parentNorm.includes(contentNorm.slice(0, 40)) ||
                        secQuoteText.includes(parentNorm.slice(0, 40)) ||
                        parentNorm.includes(secQuoteText.slice(0, 40))
                    );
                    if (!matches) continue;
                }

                const secIdMap = section.id_map;
                const secQuotes = Array.isArray(section.quote) ? section.quote : [];
                if (!secIdMap || typeof secIdMap !== "object" || Array.isArray(secIdMap)) continue;
                for (const [rid, idxs] of Object.entries(secIdMap)) {
                    if (!Array.isArray(idxs)) continue;
                    const qs = idxs.map(i => (secQuotes[i] ?? "").trim()).filter(Boolean);
                    if (qs.length) {
                        childQuoteMap[rid] = qs;
                    }
                }
            }
        }
        // Walk this summary's sources.
        const sources = node.primitives?.source?.allItems ??
                        node.primitives?.ref?.allItems ??
                        [];
        let walkedAny = false;
        for (const child of (Array.isArray(sources) ? sources : [])) {
            const cqs = childQuoteMap[child.id] ?? [];
            walkDown(child, cqs.length ? cqs : [], depth + 1);
            walkedAny = true;
        }
        // If no sources via primitives.source, try walking the structured_summary
        // ids — this is the normal path when the summary IS the top-level node
        // (e.g. flow instance selected via DSL block).
        if (!walkedAny) {
            const ss = node.referenceParameters?.structured_summary;
            if (Array.isArray(ss)) {
                for (const section of ss) {
                    const secIds = Array.isArray(section.ids) ? section.ids : [];
                    for (const sid of secIds) {
                        if (seen.has(sid)) continue;
                        let childPrim = mainstore.primitive(sid);
                        // If not in store, create a placeholder and record it for
                        // the caller to fetch asynchronously.
                        if (!childPrim) {
                            childPrim = { id: sid, type: "unknown", title: null, plainId: null };
                            missingIds.push(sid);
                        }
                        let childQuotes = [];
                        if (childQuoteMap[sid]) {
                            childQuotes = childQuoteMap[sid];
                        } else if (section.quote && section.id_map) {
                            const idxs = section.id_map[sid] ?? section.id_map[`${sid}`];
                            if (Array.isArray(idxs)) {
                                childQuotes = idxs.map(i => (section.quote[i] ?? "").trim()).filter(Boolean);
                            }
                        }
                        walkDown(childPrim, childQuotes.length ? childQuotes : [], depth + 1);
                    }
                }
            }
        }
    };

    walkDown(startPrim, quoteForEvidence, 0);
    return { chain, missingIds };
}
